import React from 'react';
import { CurrentUser, Deal, IntegrationConfig, LoanApplicationTask, Product, Question } from '../models';
import { useAuth } from './AuthContext';
import { useLocation, useParams } from 'react-router-dom';
import { DealRequestAllocation } from '../models/DealRequestAllocation';
import { ArrayUtils } from '../utils';
import {
	useDealQuery,
	useDealQuestionsQuery,
	useDealRequestAllocationQuery,
	useDealProductQuery,
	useDealRequiredDocumentsQuery,
	useIntegrationConfigQuery,
} from '../hooks';

type CerebroNavContext = {
	navItems: NavItem[];
	currentNavItem?: NavLink;
	nextNavItem?: NavLink;
	setState: (state: ReturnType<typeof buildNavItems>) => void;
};

const Context = React.createContext<CerebroNavContext | null>(null);

export const CerebroNavProvider: React.FC = ({ children }) => {
	const { currentUser } = useAuth();

	const { pathname } = useLocation();

	const [navItems, setNavItems] = React.useState<NavItem[]>([]);
	const [currentNavItem, setCurrentNavItem] = React.useState<NavLink>();
	const [nextNavItem, setNextNavItem] = React.useState<NavLink>();
	const { guid } = useParams<'guid'>();

	const { data: deal } = useDealQuery(guid);
	const { data: questions } = useDealQuestionsQuery();
	const { data: allocation } = useDealRequestAllocationQuery(guid);
	const { data: product } = useDealProductQuery(guid);
	const { data: requiredDocuments } = useDealRequiredDocumentsQuery(guid);
	const { data: integrations } = useIntegrationConfigQuery();

	const setState = ({ navItems, currentNavItem, nextNavItem }: ReturnType<typeof buildNavItems>) => {
		setNavItems(navItems);
		setCurrentNavItem(currentNavItem);
		setNextNavItem(nextNavItem);
	};

	React.useEffect(() => {
		setState(
			buildNavItems(currentUser, allocation, deal, pathname, questions, product, requiredDocuments, integrations)
		);
	}, [
		currentUser,
		allocation,
		deal,
		pathname,
		questions,
		product,
		requiredDocuments,
		integrations,
	]); /* eslint-disable-line react-hooks/exhaustive-deps */

	React.useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	const context: CerebroNavContext = {
		navItems,
		currentNavItem,
		nextNavItem,
		setState,
	};

	return <Context.Provider value={context}>{children}</Context.Provider>;
};

export function useCerebroNav() {
	var context = React.useContext(Context);

	if (context == null) throw Error('You tried to consume the CerebroNavContext outside of a CerebroNavProvider!');

	return context;
}

export const Urls = {
	Welcome: { Root: 'Welcome' },

	Deal: {
		Root: 'Request',
		Introduction: 'Request/Introduction',
		BusinessAcquisition: 'Request/Acquisition',
		DebtCapital: 'Request/Debt',
	},

	Documents: {
		Root: 'Documents',
	},

	Company: {
		Root: 'Company',
		Borrower: 'Company/Borrower',
		BusinessAndPrincipal: 'Company/Business',
		Acquisition: 'Company/Acquisition',
	},

	Debt: {
		Root: 'DebtSchedule',
		RevisionForm: 'DebtSchedule/:termSheetGuid/Revisions/:revisionGuid',
	},

	Financials: {
		Root: 'Financials',

		Borrower: {
			Root: 'Financials/Borrower',
			BasicInfo: 'Financials/Borrower/Info',
			IncomeStatement: 'Financials/Borrower/Income',
			CollateralTypes: 'Financials/Borrower/CollateralTypes',
			CollateralDetails: 'Financials/Borrower/CollateralDetails',
		},

		Acquisition: {
			Root: 'Financials/Acquisition',
			BasicInfo: 'Financials/Acquisition/Info',
			IncomeStatement: 'Financials/Acquisition/Income',
			CollateralTypes: 'Financials/Acquisition/CollateralTypes',
			CollateralDetails: 'Financials/Acquisition/CollateralDetails',
		},
	},

	ExcludedLenders: { Root: 'ExcludedLenders' },
	Questions: { Root: 'Questions' },

	DealTypePreference: { Root: 'DealTypePreference' },

	OutputAnalysis: { Root: 'OutputAnalysis' },
	Colleagues: { Root: 'Colleagues' },
	Review: { Root: 'Review' },
	DealSettings: { Root: 'DealSettings' },
	Notes: { Root: 'Notes' },
	UpdateDealStatus: { Root: 'UpdateDealStatus' },
	Matching: { Root: 'Matching' },
	Results: { Root: 'Results' },
	Status: { Root: 'Status' },
	RelatedDeals: { Root: 'RelatedDeals' },
};

export function buildNavItems(
	currentUser: CurrentUser,
	allocation: DealRequestAllocation | undefined,
	deal: Deal | undefined,
	currentPath: string,
	questions: Question[] | undefined,
	product: Product | undefined,
	requiredDocuments: LoanApplicationTask[] | undefined,
	integrations: IntegrationConfig | undefined
) {
	const isEmployeeUser = currentUser?.isEmployeeUser ?? false;

	const isAcquisition = allocation?.acquiringBusiness === true;

	const isStandaloneAcquisition = isAcquisition && allocation?.dealType === 'Acquisitions';
	const isAddOnAcquisition = isAcquisition && allocation?.dealType === 'AcquisitionAddon';
	const isBuyOutAcquisition =
		isAcquisition &&
		(allocation?.dealType === 'AcquisitionManagementBuyout' || allocation?.dealType === 'AcquisitionPartnerBuyout');
	const docOnlyView =
		!product?.isContractRequired &&
		deal?.dealStatus === 'Accepted' &&
		!isEmployeeUser &&
		requiredDocuments &&
		requiredDocuments.length > 0;
	const showExcludedLenders = isEmployeeUser || (deal && deal.excludedLenderCount > 0);

	const docOnlyNav: NavItem[] = [
		{
			text: 'Documents',

			url: Urls.Documents.Root,
			urlRootForSection: Urls.Documents.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},
		{
			text: 'Loan Options',
			url: Urls.DealTypePreference.Root,
			urlRootForSection: Urls.DealTypePreference.Root,
			absolute: false,
			completed: false,
			disabled: false,
			children: [],
		},
	];

	const mainNav: NavItem[] = [
		{
			text: 'Welcome',

			url: Urls.Welcome.Root,
			urlRootForSection: Urls.Welcome.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},

		{
			text: 'Loan Request',

			urlRootForSection: Urls.Deal.Root,
			url: Urls.Deal.Introduction,

			absolute: false,
			disabled: false,
			completed: false,

			children: [
				{
					text: 'Financing Needs',

					url: Urls.Deal.Introduction,
					urlRootForSection: Urls.Deal.Root,

					absolute: false,
					disabled: false,
					completed: false,

					children: [],
				},
				...(isAcquisition
					? [
							{
								text: 'Acquisition Details',

								url: Urls.Deal.BusinessAcquisition,
								urlRootForSection: Urls.Deal.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},
					  ]
					: []),
				...(allocation?.debtCapitalForBusiness === true
					? [
							{
								text: 'Details',

								url: Urls.Deal.DebtCapital,
								urlRootForSection: Urls.Deal.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},
					  ]
					: []),
			],
		},

		{
			text: 'Company',

			urlRootForSection: Urls.Company.Root,
			url:
				!isAcquisition || isAddOnAcquisition || isStandaloneAcquisition
					? Urls.Company.Borrower
					: Urls.Company.Acquisition,

			absolute: false,
			disabled: false,
			completed: false,

			children: [
				...(!isAcquisition || isAddOnAcquisition || isStandaloneAcquisition
					? [
							{
								text: 'Borrower',

								url: Urls.Company.Borrower,
								urlRootForSection: Urls.Company.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},
					  ]
					: []),

				...(isBuyOutAcquisition || isAddOnAcquisition
					? [
							{
								text: 'Acquisition Target',

								url: Urls.Company.Acquisition,
								urlRootForSection: Urls.Company.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},
					  ]
					: []),

				{
					text: 'Narrative',

					url: Urls.Company.BusinessAndPrincipal,
					urlRootForSection: Urls.Company.Root,

					absolute: false,
					disabled: false,
					completed: false,

					children: [],
				},
			],
		},

		{
			text: 'Existing Debt',

			url: Urls.Debt.Root,
			urlRootForSection: Urls.Debt.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},

		{
			text: 'Financials',

			urlRootForSection: Urls.Financials.Root,
			url:
				!isAcquisition || isAddOnAcquisition || isStandaloneAcquisition
					? Urls.Financials.Borrower.BasicInfo
					: Urls.Financials.Acquisition.BasicInfo,

			absolute: false,
			disabled: false,
			completed: false,

			children: [
				...(!isAcquisition || isAddOnAcquisition || isStandaloneAcquisition
					? [
							{
								text: 'Borrower',

								url: Urls.Financials.Borrower.BasicInfo,
								urlRootForSection: Urls.Financials.Borrower.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [
									{
										text: 'Basic Info',

										url: Urls.Financials.Borrower.BasicInfo,
										urlRootForSection: Urls.Financials.Borrower.Root,

										absolute: false,
										disabled: false,
										completed: false,

										children: [],
									},
									{
										text: 'Income Statement',

										url: Urls.Financials.Borrower.IncomeStatement,
										urlRootForSection: Urls.Financials.Borrower.Root,

										absolute: false,
										disabled: false,
										completed: false,

										children: [],
									},
									{
										text: 'Collateral Types',

										url: Urls.Financials.Borrower.CollateralTypes,
										urlRootForSection: Urls.Financials.Borrower.Root,

										absolute: false,
										disabled: false,
										completed: false,
										children: [],
									},
									{
										text: 'Collateral Details',

										url: Urls.Financials.Borrower.CollateralDetails,
										urlRootForSection: Urls.Financials.Borrower.Root,

										absolute: false,
										disabled: false,
										completed: false,
										children: [],
									},
								],
							},
					  ]
					: []),

				...(isBuyOutAcquisition || isAddOnAcquisition
					? [
							{
								text: 'Acquisition Target',

								url: Urls.Financials.Acquisition.BasicInfo,
								urlRootForSection: Urls.Financials.Acquisition.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [
									{
										text: 'Basic Info',

										url: Urls.Financials.Acquisition.BasicInfo,
										urlRootForSection: Urls.Financials.Acquisition.Root,

										absolute: false,
										disabled: false,
										completed: false,

										children: [],
									},
									{
										text: 'Income Statement',

										url: Urls.Financials.Acquisition.IncomeStatement,
										urlRootForSection: Urls.Financials.Acquisition.Root,

										absolute: false,
										disabled: false,
										completed: false,

										children: [],
									},
									{
										text: 'Collateral Types',

										url: Urls.Financials.Acquisition.CollateralTypes,
										urlRootForSection: Urls.Financials.Acquisition.Root,

										absolute: false,
										disabled: false,
										completed: false,
										children: [],
									},
									{
										text: 'Collateral Details',

										url: Urls.Financials.Acquisition.CollateralDetails,
										urlRootForSection: Urls.Financials.Acquisition.Root,

										absolute: false,
										disabled: false,
										completed: false,
										children: [],
									},
								],
							},
					  ]
					: []),
			],
		},

		...(showExcludedLenders
			? [
					{
						text: 'Exclude Lenders',
						url: Urls.ExcludedLenders.Root,
						urlRootForSection: Urls.ExcludedLenders.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
			  ]
			: []),

		{
			text: 'Loan Options',
			url: Urls.DealTypePreference.Root,
			urlRootForSection: Urls.DealTypePreference.Root,
			absolute: false,
			completed: false,
			disabled: false,
			children: [],
		},

		{
			text: 'Documents',

			url: Urls.Documents.Root,
			urlRootForSection: Urls.Documents.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},

		...(deal?.dealStatus === 'Started'
			? [
					{
						text: 'Review',

						url: Urls.Review.Root,
						urlRootForSection: Urls.Review.Root,

						absolute: false,
						completed: false,
						disabled: false,

						children: [],
					},
			  ]
			: []),
		...(product === undefined ||
		product?.isContractRequired ||
		(product !== undefined && !product?.isContractRequired && deal?.dealStatus !== 'Accepted')
			? [
					{
						text: 'Summary',

						url: Urls.Results.Root,
						urlRootForSection: Urls.Results.Root,

						absolute: false,
						completed: false,
						disabled: deal?.dealStatus === 'Started',

						tooltip: () => (deal?.dealStatus === 'Started' ? 'Available upon submission' : undefined),

						children: [],
					},
			  ]
			: []),

		{ key: '01_Separator', isSeparator: true },

		...(questions && questions.length > 0
			? [
					{
						text: 'Questions',
						url: Urls.Questions.Root,
						urlRootForSection: Urls.Questions.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
			  ]
			: []),
		{
			text: 'Credit Analysis',

			url: Urls.OutputAnalysis.Root,
			urlRootForSection: Urls.OutputAnalysis.Root,

			absolute: false,
			completed: false,
			disabled: false,

			children: [],
		},
		{
			text: 'Colleagues',

			url: Urls.Colleagues.Root,
			urlRootForSection: Urls.Colleagues.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},

		...(isEmployeeUser ||
		ArrayUtils.NotOneOf(deal?.dealStatus, 'Started', 'Accepted', 'Declined', 'MoreInfoNeeded', 'ReadyForReview')
			? ([{ key: '02_Separator', isSeparator: true }] as NavItem[])
			: []),

		...(isEmployeeUser
			? [
					{
						text: 'Deal Settings',
						url: Urls.DealSettings.Root,
						urlRootForSection: Urls.DealSettings.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
					{
						text: 'Employee Notes',
						url: Urls.Notes.Root,
						urlRootForSection: Urls.Notes.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
					{
						text: 'Matching',
						url: Urls.Matching.Root,
						urlRootForSection: Urls.Matching.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
					{
						text: 'Deal Status',
						url: Urls.UpdateDealStatus.Root,
						urlRootForSection: Urls.UpdateDealStatus.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
			  ]
			: []),
	];

	const navItems = docOnlyView ? docOnlyNav : mainNav;

	const flattened = navItems
		.filter(isNavLink)
		.filter(navLink => !navLink.disabled)
		.flatMap(flatMapLinks);
	const currentIndex = flattened.findIndex(navItem => currentPath.endsWith(navItem.url));
	const currentNavItem = currentIndex > -1 ? flattened[currentIndex] : undefined;
	const nextNavItem =
		currentIndex > -1 && currentIndex + 1 < flattened.length ? flattened[currentIndex + 1] : undefined;

	return { navItems, currentNavItem, nextNavItem };
}

export type NavItem = NavLink | NavSeparator;

interface NavLink {
	text: string;

	url: string;
	urlRootForSection?: string | undefined;

	disabled: boolean;
	completed: boolean;
	absolute: boolean;

	tooltip?: (navLink: NavLink) => string | undefined;

	children: NavItem[];
}

interface NavSeparator {
	key: string;
	isSeparator: true;
}

export function isSeparator(navItem: NavItem): navItem is NavSeparator {
	return (navItem as NavSeparator)?.isSeparator === true;
}

export function isNavLink(navItem: NavItem): navItem is NavLink {
	return (navItem as NavLink)?.url !== undefined;
}

export function flatMapLinks(link: NavLink): NavLink[] {
	return link.children.length > 0 ? link.children.filter(isNavLink).flatMap(flatMapLinks) : [link];
}
